
import { Filter, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { useState } from "react";

interface LoanFiltersProps {
  onFilterChange: (type: string) => void;
  onCollateralFilterChange: (hasCollateral: boolean) => void;
  onSearchChange?: (searchTerm: string) => void;
  onAmountFilterChange?: (minAmount: number, maxAmount: number) => void;
  onSortChange?: (sortBy: string) => void;
}

export const LoanFilters = ({ 
  onFilterChange, 
  onCollateralFilterChange,
  onSearchChange,
  onAmountFilterChange,
  onSortChange
}: LoanFiltersProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [amountRange, setAmountRange] = useState([0, 50000]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (onSearchChange) {
      onSearchChange(e.target.value);
    }
  };

  const handleAmountChange = (values: number[]) => {
    setAmountRange(values);
    if (onAmountFilterChange) {
      onAmountFilterChange(values[0], values[1]);
    }
  };

  return (
    <div className="space-y-4 mb-6 rounded-xl bg-gradient-to-br from-white to-slate-50 shadow-md border border-slate-200 p-4">
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium text-slate-700">Filters:</span>
        </div>
        
        <div className="w-full md:w-auto relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-primary/70" />
          <Input
            placeholder="Search by purpose or description..."
            className="pl-9 pr-4 w-full md:w-[300px] border-slate-200 focus:border-primary/40 bg-white shadow-sm"
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <Select onValueChange={onFilterChange} defaultValue="all">
          <SelectTrigger className="bg-white border-slate-200 focus:ring-primary/30 shadow-sm hover:bg-slate-50 transition-colors">
            <SelectValue placeholder="Loan Type" />
          </SelectTrigger>
          <SelectContent className="bg-white border-slate-200 shadow-md">
            <SelectItem value="all">All Loans</SelectItem>
            <SelectItem value="business">Business Loans</SelectItem>
            <SelectItem value="personal">Personal Loans</SelectItem>
          </SelectContent>
        </Select>
        
        <Select onValueChange={(value) => onCollateralFilterChange(value === 'true')} defaultValue="all">
          <SelectTrigger className="bg-white border-slate-200 focus:ring-primary/30 shadow-sm hover:bg-slate-50 transition-colors">
            <SelectValue placeholder="Collateral Status" />
          </SelectTrigger>
          <SelectContent className="bg-white border-slate-200 shadow-md">
            <SelectItem value="all">All Loans</SelectItem>
            <SelectItem value="true">Property Backed</SelectItem>
            <SelectItem value="false">Unsecured</SelectItem>
          </SelectContent>
        </Select>
        
        <Select onValueChange={onSortChange || (() => {})} defaultValue="newest">
          <SelectTrigger className="bg-white border-slate-200 focus:ring-primary/30 shadow-sm hover:bg-slate-50 transition-colors">
            <SelectValue placeholder="Sort By" />
          </SelectTrigger>
          <SelectContent className="bg-white border-slate-200 shadow-md">
            <SelectItem value="newest">Newest First</SelectItem>
            <SelectItem value="oldest">Oldest First</SelectItem>
            <SelectItem value="amount_high">Amount: High to Low</SelectItem>
            <SelectItem value="amount_low">Amount: Low to High</SelectItem>
            <SelectItem value="funding">Funding Progress</SelectItem>
          </SelectContent>
        </Select>
        
        <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
          <PopoverTrigger asChild>
            <Button 
              variant="outline" 
              className="w-full bg-white border-slate-200 hover:bg-slate-50 transition-all shadow-sm text-primary"
            >
              <Filter className="mr-2 h-4 w-4 text-primary" />
              Advanced Filters
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-5 bg-white border border-slate-200 shadow-lg rounded-lg z-50">
            <div className="space-y-4">
              <h4 className="font-medium text-slate-800 flex items-center">
                <Filter className="h-4 w-4 mr-2 text-primary" />
                Advanced Filters
              </h4>
              
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label className="text-slate-700">Loan Amount</Label>
                  <span className="text-sm text-slate-600 font-medium">
                    {formatCurrency(amountRange[0], "GHS")} - {formatCurrency(amountRange[1], "GHS")}
                  </span>
                </div>
                <Slider
                  defaultValue={[0, 50000]}
                  max={100000}
                  step={1000}
                  value={amountRange}
                  onValueChange={handleAmountChange}
                  className="py-4"
                />
              </div>
              
              <div className="flex justify-end pt-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="mr-2 border-slate-200 bg-white"
                  onClick={() => {
                    setAmountRange([0, 50000]);
                    setSearchTerm('');
                    if (onAmountFilterChange) onAmountFilterChange(0, 50000);
                    if (onSearchChange) onSearchChange('');
                    setIsFilterOpen(false);
                  }}
                >
                  Reset
                </Button>
                <Button 
                  size="sm"
                  className="bg-primary hover:bg-primary/90"
                  onClick={() => setIsFilterOpen(false)}
                >
                  Apply
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

// Helper function to format currency
const formatCurrency = (amount: number, currency = "GHS") => {
  return new Intl.NumberFormat("en-GH", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
  }).format(amount);
};
